import React, { useEffect, useState } from 'react';
import {
  Alert,
  Amount,
  Badge,
  Box,
  Button,
  CloseIcon,
  EditIcon,
  Heading,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ProgressBar,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  TextInput,
  useToast,
} from '@razorpay/blade/components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import {
  EMI_STATUS_COLOR,
  EMI_STATUS_LABEL,
  RECOVERY_LOAN_DETAIL_KEY,
  FULL_RECOVERY_AMOUNT_KEY,
  PARTIAL_RECOVERY_PREVIEW_KEY,
} from './constants';
import { formatMonthYearShort, normalizeLoanDetail } from './utils';
import {
  RecordRecoveryModalProps,
  LoanDetail,
  RecoveryScheduleEntry,
  RecoveryScheduleTableNode,
} from './types';
import { RecoverySuccessModal } from './RecoverySuccessModal';
import { ACTION_MODAL_Z_INDEX } from 'components/SalaryStructures/creation/components/styles';

export const RecordRecoveryModal = ({
  isOpen,
  onDismiss,
  loanId,
  loan,
  onSuccess,
  activeDrawerQueryKey,
}: RecordRecoveryModalProps) => {
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();
  const [recoveryStep, setRecoveryStep] = useState<1 | 2>(1);
  const [recoveryType, setRecoveryType] = useState<'full' | 'partial'>('full');
  const [recoveryAmount, setRecoveryAmount] = useState('');
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const handleClose = () => {
    onDismiss();
    setRecoveryStep(1);
  };

  const { data: loanDetailData } = useQuery({
    queryKey: [RECOVERY_LOAN_DETAIL_KEY, loanId],
    queryFn: () => api.loans.getLoanDetail(loanId!),
    enabled: isOpen && !!loanId,
  });
  const loanDetail = normalizeLoanDetail<LoanDetail>(loanDetailData);

  const { data: fullRecoveryData } = useQuery({
    queryKey: [FULL_RECOVERY_AMOUNT_KEY, loanId],
    queryFn: () => api.loans.getFullRecoveryAmountForLoan(loanId!),
    enabled: isOpen && !!loanId,
  });
  const fullRecoveryAmount = fullRecoveryData?.amount ?? loan?.remaining_amount_to_be_paid ?? 0;

  const {
    data: partialPreviewData,
    isError: isPartialPreviewError,
    error: partialPreviewError,
    isFetching: isPartialPreviewFetching,
  } = useQuery({
    queryKey: [PARTIAL_RECOVERY_PREVIEW_KEY, loanId, recoveryAmount],
    queryFn: () =>
      api.loans.recoveryPreview(loanId!, 'partial-recovery', undefined, Number(recoveryAmount)),
    enabled:
      recoveryStep === 2 &&
      recoveryType === 'partial' &&
      !!recoveryAmount &&
      Number(recoveryAmount) > 0,
  });
  const rawPartialSchedule: RecoveryScheduleEntry[] = partialPreviewData?.data ?? [];
  const partialSchedule: RecoveryScheduleTableNode[] = rawPartialSchedule.map((entry, idx) => ({
    ...entry,
    id: entry.id ?? idx,
  }));
  const partialRemaining =
    rawPartialSchedule.length > 0
      ? rawPartialSchedule[rawPartialSchedule.length - 1].remaining_total_amount
      : undefined;
  const partialPreviewErrorMessage = isPartialPreviewError
    ? partialPreviewError instanceof Error
      ? partialPreviewError.message
      : 'Failed to load preview.'
    : null;

  useEffect(() => {
    if (isOpen) {
      setRecoveryAmount(recoveryType === 'full' ? String(fullRecoveryAmount) : '');
    }
  }, [recoveryType, isOpen, fullRecoveryAmount]);

  const recoveryMutation = useMutation({
    mutationFn: () => {
      const action = recoveryType === 'full' ? 'full-recovery' : 'partial-recovery';
      return api.loans.editLoan({ action, amount: Number(recoveryAmount) }, loanId!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['LOANS']);
      queryClient.invalidateQueries(activeDrawerQueryKey);
      handleClose();
      setIsSuccessOpen(true);
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to record recovery. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
    const msg =
      recoveryType === 'full'
        ? `Loan ID #${loanId} has been marked as closed!`
        : `Partial recovery for Loan ID #${loanId} has been recorded!`;
    showToast({ content: msg, color: 'positive', duration: 4000 });
    onSuccess();
  };

  const getDescription = () => {
    const employeeName = loanDetail?.name ?? loan?.name ?? 'employee';
    if (recoveryType === 'full') {
      const tenure = loanDetail?.tenure ?? loan?.tenure ?? '—';
      return `All ${tenure} EMIs for ${employeeName} have been settled. EMI deductions will stop from the next payroll run.`;
    } else {
      const amount = Number(recoveryAmount).toLocaleString('en-IN');
      return `Partial recovery of ₹${amount} has been recorded for ${employeeName}. Please ensure the amount is recovered externally.`;
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onDismiss={handleClose} size="full" zIndex={ACTION_MODAL_Z_INDEX}>
        <Box borderBottomWidth="thin" borderColor="surface.border.gray.subtle">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            paddingX="spacing.6"
            paddingY="spacing.4">
            <Text size="medium" weight="semibold" color="surface.text.gray.normal">
              Record Recovery
            </Text>
            <IconButton icon={CloseIcon} accessibilityLabel="Close" onClick={handleClose} />
          </Box>
          <ProgressBar
            accessibilityLabel={`Step ${recoveryStep} of 2`}
            variant="meter"
            value={recoveryStep === 1 ? 50 : 100}
          />
        </Box>
        <ModalBody height="100%">
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Box
              display="flex"
              flexDirection="column"
              width="600px"
              backgroundColor="surface.background.gray.intense"
              borderWidth="thin"
              borderRadius="medium"
              borderColor="surface.border.gray.subtle"
              height="100%"
              overflow="hidden">
              <Box display="flex" flexDirection="column" gap="spacing.8" overflowY="auto" flex="1">
                <Box
                  paddingX="spacing.7"
                  paddingTop="spacing.8"
                  display="flex"
                  flexDirection="column"
                  gap="spacing.8">
                  {recoveryStep === 1 ? (
                    <>
                      <Box display="flex" flexDirection="column" gap="spacing.2">
                        <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
                          Recovery Type & amount
                        </Heading>
                        <Text size="small" color="surface.text.gray.subtle">
                          Choose the vendor to whom you would like to make the payment
                        </Text>
                      </Box>
                      <Box display="flex" flexDirection="column" gap="spacing.5">
                        <RadioGroup
                          label="Loan recovery type"
                          value={recoveryType}
                          onChange={({ value }) => {
                            if (value === 'full' || value === 'partial') {
                              setRecoveryType(value);
                            }
                          }}>
                          <Box display="flex" flexDirection="column" gap="spacing.3">
                            <Box
                              borderWidth="thin"
                              borderRadius="medium"
                              borderColor={
                                recoveryType === 'full'
                                  ? 'surface.border.primary.normal'
                                  : 'surface.border.gray.subtle'
                              }
                              padding="spacing.4">
                              <Radio
                                value="full"
                                helpText="Recover the entire outstanding loan amount.">
                                Full Recovery
                              </Radio>
                            </Box>
                            <Box
                              borderWidth="thin"
                              borderRadius="medium"
                              borderColor={
                                recoveryType === 'partial'
                                  ? 'surface.border.primary.normal'
                                  : 'surface.border.gray.subtle'
                              }
                              padding="spacing.4">
                              <Radio
                                value="partial"
                                helpText="Recover a partial amount from the outstanding loan amount.">
                                Partial Recovery
                              </Radio>
                            </Box>
                          </Box>
                        </RadioGroup>
                        <TextInput
                          label="Total amount to be recovered"
                          prefix="₹"
                          type="number"
                          value={recoveryAmount}
                          isDisabled={recoveryType === 'full'}
                          onChange={({ value }) => {
                            const cleaned = (value ?? '').replace(/[^0-9]/g, '');
                            setRecoveryAmount(cleaned);
                          }}
                        />
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box display="flex" flexDirection="column" gap="spacing.2">
                        <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
                          Verify & Confirm
                        </Heading>
                        <Text size="small" color="surface.text.gray.subtle">
                          {recoveryType === 'partial'
                            ? 'Review partial recovery details. The loan stays active, and remaining EMIs will continue as scheduled.'
                            : 'Review recovery details'}
                        </Text>
                      </Box>
                      <Box display="flex" flexDirection="column" gap="spacing.3">
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          marginBottom="spacing.2">
                          <Text size="small" weight="semibold" color="surface.text.gray.normal">
                            Recovery Details
                          </Text>
                          <Link icon={EditIcon} size="small" onClick={() => setRecoveryStep(1)}>
                            Edit
                          </Link>
                        </Box>
                        {[
                          {
                            label: 'Recovery Type',
                            value: recoveryType === 'full' ? 'Full Recovery' : 'Partial Recovery',
                          },
                          {
                            label: 'Full Outstanding Amount',
                            value: `₹${Number(recoveryAmount).toLocaleString('en-IN')}`,
                          },
                          { label: 'Mode of Recovery', value: 'External' },
                        ].map(({ label, value }) => (
                          <Box
                            key={label}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center">
                            <Text size="small" weight="medium" color="surface.text.gray.muted">
                              {label}
                            </Text>
                            <Box display="flex" width="50%" alignItems="end">
                              <Text size="small" weight="medium" color="surface.text.gray.muted">
                                {value}
                              </Text>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>

                {recoveryStep === 2 &&
                  recoveryType === 'partial' &&
                  !isPartialPreviewFetching &&
                  !!partialPreviewErrorMessage && (
                    <Box paddingX="spacing.7">
                      <Alert
                        color="negative"
                        isDismissible={false}
                        isFullWidth
                        description={partialPreviewErrorMessage}
                      />
                    </Box>
                  )}

                {recoveryStep === 2 &&
                  recoveryType === 'partial' &&
                  !isPartialPreviewFetching &&
                  !isPartialPreviewError &&
                  partialSchedule.length > 0 && (
                    <Box paddingX="spacing.7" display="flex" flexDirection="column">
                      <Heading size="medium" weight="semibold" marginBottom="spacing.4">
                        Preview
                      </Heading>
                      <Table<RecoveryScheduleTableNode>
                        height="250px"
                        data={{ nodes: partialSchedule }}
                        gridTemplateColumns="1fr 1fr 1fr 1fr">
                        {(rows) => (
                          <>
                            <TableHeader>
                              <TableHeaderRow>
                                <TableHeaderCell>Month</TableHeaderCell>
                                <TableHeaderCell textAlign="right">EMI</TableHeaderCell>
                                <TableHeaderCell textAlign="right">Outstanding</TableHeaderCell>
                                <TableHeaderCell>Status</TableHeaderCell>
                              </TableHeaderRow>
                            </TableHeader>
                            <TableBody>
                              {rows.map((entry) => (
                                <TableRow key={entry.id} item={entry}>
                                  <TableCell>
                                    <Text size="small">
                                      {formatMonthYearShort(entry.payroll_month ?? entry.month)}
                                    </Text>
                                  </TableCell>
                                  <TableCell textAlign="right">
                                    <Amount
                                      value={entry.emi_amount ?? entry.total_emi ?? 0}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell textAlign="right">
                                    <Amount
                                      value={
                                        entry.remaining_total_amount ?? entry.principal_emi ?? 0
                                      }
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      color={EMI_STATUS_COLOR[entry.status] ?? 'neutral'}
                                      size="small">
                                      {EMI_STATUS_LABEL[entry.status] ?? 'Pending'}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </>
                        )}
                      </Table>
                    </Box>
                  )}

                {recoveryStep === 2 && (
                  <Box paddingX="spacing.7" marginBottom="spacing.4">
                    <Alert
                      color="information"
                      isDismissible={false}
                      description={
                        recoveryType === 'partial'
                          ? `A partial payment of ₹${Number(recoveryAmount).toLocaleString(
                              'en-IN',
                            )} will be recorded externally. The outstanding balance will reduce to ₹${(
                              partialRemaining ?? 0
                            ).toLocaleString(
                              'en-IN',
                            )}, and the loan will remain active with future EMIs continuing as scheduled.`
                          : `The outstanding amount of ₹${Number(recoveryAmount).toLocaleString(
                              'en-IN',
                            )} will be recovered externally. The employee's loan will be closed, and the loan status will be updated after recovery.`
                      }
                    />
                  </Box>
                )}
              </Box>

              <Box
                borderTopWidth="thin"
                display="flex"
                justifyContent="space-between"
                width="100%"
                paddingX="spacing.8"
                paddingY="spacing.5">
                <Button
                  variant="secondary"
                  onClick={recoveryStep === 2 ? () => setRecoveryStep(1) : handleClose}>
                  {recoveryStep === 2 ? 'Back' : 'Close'}
                </Button>
                {recoveryStep === 1 ? (
                  <Button
                    isDisabled={!recoveryAmount || Number(recoveryAmount) <= 0}
                    onClick={() => setRecoveryStep(2)}>
                    Next
                  </Button>
                ) : (
                  <Button
                    isLoading={recoveryMutation.isLoading}
                    isDisabled={!!(recoveryType === 'partial' && partialPreviewErrorMessage)}
                    onClick={() => recoveryMutation.mutate()}>
                    Confirm
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </ModalBody>
      </Modal>

      <RecoverySuccessModal
        isOpen={isSuccessOpen}
        onDismiss={handleSuccessClose}
        loanId={loanId}
        title={recoveryType === 'full' ? 'Marked as closed!' : 'Partial recovery recorded!'}
        description={getDescription()}
      />
    </>
  );
};
